// src/components/JarvislyModule/DocumentProvider.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import useApiInitializer from 'hooks/useApiInitializer';
import useApi from 'hooks/useApi';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isMongoObjectId } from 'utils/helpers';
import { useModuleContext } from './ModuleProvider';
import useZustand from 'hooks/useZustand';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getQuery, updateQuery } from 'utils/urlUtils';
import useUrl from 'hooks/useUrl';
import { parseImportToken, QUERY_PARAM_NAME } from 'utils/crossProfileImport';
import BackToHomeLink from 'components/core/BackToHomeLink';
import DocumentIdentifier from 'components/core/DocumentIdentifier';

// *******************************************************************************
// CONTEXT
// *******************************************************************************
const DocumentContext = createContext({});
export const useDocumentContext = () => useContext(DocumentContext);

// *******************************************************************************
// PROVIDER
// *******************************************************************************
const DocumentProvider = ({ children, hideBackToHomeLink }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedPageSettings = useZustand('module', 'pageSettings');
  const storedCurrentPage = useZustand('module', 'currentPage');
  const setViewModeStore = useZustand('module', 'setViewMode');
  const setGlobalStore = useZustand('global', 'setState');

  const { registerProviderContext, setDocumentState } = useModuleContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const parsedUrl = useUrl();
  const { params, api } = parsedUrl;
  const [searchParams] = useSearchParams();

  // Check for cross-profile import token
  const importToken = searchParams.get(QUERY_PARAM_NAME);
  const importData = useMemo(() => parseImportToken(importToken), [importToken]);

  // Initialize API global state
  useApiInitializer();

  // Use useApi to leverage global state and setReady handling
  // Pass paramKey and params so useApi can extract _id from URL params
  // (useApi can't use useDocumentContext here because we ARE the provider - context isn't set yet)
  // When autoFetch: false in config, useApi will skip execution (page component handles its own API calls)
  // IMPORTANT: Check if paramKey is explicitly defined in routing config
  // If paramKey is null, it means the page doesn't fetch by ID (like Welcome page)
  // If paramKey is undefined, default to '_id'
  const routingParamKey = storedCurrentPage?.routing?.paramKey;
  const paramKey = routingParamKey === undefined ? '_id' : routingParamKey;

  useApi({ hideLoading: false, paramKey, params });

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const data = storedCurrentPage?.data;
  const initTab = storedPageSettings?.initialTab;

  // Get refresh function from global API store
  const apiGlobal = useZustand('global', 'useApi');
  const refresh = apiGlobal?.refresh;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [availableTabs, setAvailableTabs] = useState([]);
  const [tab, setTab] = useState(getInitialTab(initTab));
  const [viewMode, setViewModeInternal] = useState('view');

  // Field-to-tab mapping: maps field name prefixes to tab keys
  // Example: { 'fiscalData': 'fiscal', 'address': 'record' }
  const [tabFieldPrefixes, setTabFieldPrefixes] = useState({});

  // Ref to always have the latest tab value for navigateToFieldTab
  // This avoids stale closure issues when the function is stored in zustand
  const tabRef = useRef(tab);
  tabRef.current = tab;

  // PERFORMANCE FIX: Ref to store the actual navigateToFieldTab function
  // This allows us to expose a stable wrapper that never changes reference
  const navigateToFieldTabRef = useRef(null);

  // Wrapper to sync viewMode with documentState.mode in ModuleProvider and module store
  const setViewMode = useCallback(
    mode => {
      setViewModeInternal(mode);
      // Sync mode to ModuleProvider's documentState for ActionButton disabled state
      setDocumentState?.(prev => ({ ...prev, mode }));
      // Sync mode to module store for setPageData and other store functions
      setViewModeStore?.(mode);
    },
    [setDocumentState, setViewModeStore]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // Get paramKey from page API config, fallback to '_id'
    const paramKey = storedCurrentPage?.routing?.paramKey || '_id';

    const _id = params?.[paramKey] || params?._id;
    let mode = 'view';

    if (_id) {
      if (_id === 'add') {
        // New document - always 'add' mode
        mode = 'add';
      } else if (isMongoObjectId(_id)) {
        // CROSS-PROFILE IMPORT: Force 'edit' mode when import token is present
        // This allows user to save and add the new profile to dataProfile
        if (importData && importData.entityId === _id) {
          mode = 'edit';
        } else if (searchParams.get('mode') === 'edit') {
          // URL param ?mode=edit: force edit mode (used when redirecting to a newly created draft)
          mode = 'edit';
        } else {
          // Existing document - check viewModeOnOpen setting
          // Default is 'view' (locked) for backwards compatibility
          const viewModeOnOpen = storedCurrentPage?.settings?.viewModeOnOpen;
          mode = viewModeOnOpen === 'edit' ? 'edit' : 'view';
        }
      }
    } else {
      mode = 'view';
    }

    // Update viewMode state (wrapper also syncs to documentState.mode)
    setViewMode(mode);
  }, [
    api,
    params,
    storedCurrentPage?.routing?.paramKey,
    storedCurrentPage?.settings?.viewModeOnOpen,
    setViewMode,
    importData,
    searchParams,
  ]);

  useEffect(() => {
    setDocumentState?.(prev => ({
      ...prev,
      folder: data?._metadata?.audit?.deletedAt
        ? 'deleted'
        : data?._metadata?.audit?.removedAt
          ? 'recycle'
          : data?._metadata?.audit?.filedAt
            ? 'archive'
            : 'inbox',
    }));
  }, [data, setDocumentState]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const buildTabs = useCallback(
    tabsArr => {
      if (!initTab) return [];

      const at = tabsArr
        .filter(x => x && !x.hide)
        .map(x => {
          if (typeof x.forceRender === 'undefined') x.forceRender = true;
          return x;
        });

      const tabFromUrl = getQuery()?.tab;

      const { key: selectedTab = initTab } =
        at?.find(t => t?.key === tabFromUrl && !t?.disabled) || {};

      setAvailableTabs(at);
      setTab(selectedTab);

      // CRITICAL: Only update URL if the tab is different from what's in the URL
      // This prevents unnecessary re-renders that can cause infinite loops
      // when navigating with ?tab= query parameter
      if (tabFromUrl !== selectedTab) {
        updateQuery({ tab: selectedTab });
      }
    },
    [initTab]
  );

  const handleTabChange = useCallback(value => {
    setTab(value);
    updateQuery({ tab: value });
  }, []);

  /**
   * Register field prefixes for a tab
   * Used to determine which tab contains a specific field for error navigation
   * @param {Object} prefixes - Object mapping field prefixes to tab keys
   * @example registerTabFieldPrefixes({ 'fiscalData': 'fiscal', 'address': 'record' })
   */
  const registerTabFieldPrefixes = useCallback(prefixes => {
    if (!prefixes || typeof prefixes !== 'object') return;
    setTabFieldPrefixes(prev => ({ ...prev, ...prefixes }));
  }, []);

  /**
   * Navigate to the tab containing a specific field
   * Used when there's a validation error to show the user the correct tab
   * @param {string} fieldName - The field name (e.g., 'fiscalData.nfse.cnae')
   * @returns {boolean} - Whether navigation was successful
   */
  const navigateToFieldTabImpl = useCallback(
    fieldName => {
      if (!fieldName) return false;

      // Flatten field name if it's an array (Ant Design format)
      const flatFieldName = Array.isArray(fieldName) ? fieldName.join('.') : fieldName;

      // Use tabRef.current to always get the latest tab value
      // This avoids stale closure issues when function is stored in zustand
      const currentTab = tabRef.current;

      // Check direct match first
      if (tabFieldPrefixes[flatFieldName]) {
        const targetTab = tabFieldPrefixes[flatFieldName];
        if (targetTab !== currentTab) {
          handleTabChange(targetTab);
          return true;
        }
        return false; // Already on the correct tab
      }

      // Check prefix match (e.g., 'fiscalData.nfse.cnae' matches 'fiscalData')
      // Sort prefixes by length (longest first) to match most specific prefix
      const sortedPrefixes = Object.keys(tabFieldPrefixes).sort((a, b) => b.length - a.length);

      for (const prefix of sortedPrefixes) {
        if (flatFieldName.startsWith(prefix)) {
          const targetTab = tabFieldPrefixes[prefix];
          if (targetTab !== currentTab) {
            handleTabChange(targetTab);
            return true;
          }
          return false; // Already on the correct tab
        }
      }

      return false; // No matching tab found
    },
    [tabFieldPrefixes, handleTabChange]
  );

  // Update ref when implementation changes
  navigateToFieldTabRef.current = navigateToFieldTabImpl;

  // PERFORMANCE FIX: Stable wrapper function that never changes reference
  // This prevents re-renders in consumers when the implementation updates
  const navigateToFieldTab = useCallback(
    fieldName => navigateToFieldTabRef.current?.(fieldName),
    []
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Exposed values for DocumentContext consumers
  const exposedValues = useMemo(
    () => ({
      documentContext: true,
      initTab,
      tab,
      handleTabChange,
      buildTabs,
      availableTabs,
      viewMode,
      setViewMode,
      data,
      refresh,
      // Tab navigation for validation errors
      registerTabFieldPrefixes,
      navigateToFieldTab,
      ...parsedUrl,
    }),
    [
      initTab,
      tab,
      handleTabChange,
      buildTabs,
      availableTabs,
      viewMode,
      setViewMode,
      data,
      refresh,
      registerTabFieldPrefixes,
      navigateToFieldTab,
      parsedUrl,
    ]
  );

  // NOTE: navigateToFieldTab is now accessed via useDocumentContext() directly
  // No need to store in zustand - the stable wrapper function prevents re-renders

  // Keep a ref to the latest exposedValues to avoid infinite loops
  const exposedValuesRef = useRef(exposedValues);
  exposedValuesRef.current = exposedValues;

  // Register document context in ModuleProvider
  // IMPORTANT: We intentionally do NOT include exposedValues in dependencies
  // to prevent infinite loops. The registration happens once on mount,
  // and consumers should use useDocumentContext() for reactive updates.
  useEffect(() => {
    registerProviderContext?.('documentContext', exposedValuesRef.current);

    return () => {
      registerProviderContext?.('documentContext', null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerProviderContext]);

  // NOTE: Removed Zustand sync to avoid potential Redux DevTools serialization issues
  // Use React DevTools or useDocumentContext() hook to inspect context values instead

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <DocumentContext.Provider value={exposedValues}>
      {children}

      <DocumentIdentifier data={data} />

      {!hideBackToHomeLink ? <BackToHomeLink /> : null}
    </DocumentContext.Provider>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DocumentProvider;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function getInitialTab(queryTab, initTab) {
  if (queryTab && queryTab !== 'undefined' && typeof queryTab === 'string') return queryTab;
  return initTab;
}
